import { doc, updateDoc, setDoc, getDoc, getDocs, getDocFromServer, increment, query, collection, runTransaction} from "firebase/firestore";

import {db, logAnalytics} from '../firebase';
import * as defs from '../util/definitions';
var ls = require('local-storage');

/*
schema for game

game
    id: numerical identifier
    tiles: string of characters
    score: top score
*/

export function getCurrentGame(response) {
    // DEBUG check if the game id is passed as a query string
    const qp = new URLSearchParams(window.location.search);
    if ( qp.get("game") ) {
        response(qp.get("game"));
        return
    }
    const docRef = doc(db, "todaygame", "1");
    const docSnap = getDoc(docRef).then((docSnap) => {
        if (docSnap.exists()) {
            //console.log("Document data:", docSnap.data());
            let game = docSnap.data();
            response(game.game);
        } else {
            // doc.data() will be undefined in this case
            //console.log("No such document!");
        }
    });
}

export function getGame(game, response) {
    const docRef = doc(db, "games", String(game));
    const docSnap = getDoc(docRef).then((docSnap) => {
        if (docSnap.exists()) {
            //console.log("Document data:", docSnap.data());
            response(docSnap.data());
        } else {
            // doc.data() will be undefined in this case
            //console.log("No such document!");
        }
    });
}

export function logStartGame(game) {
    setDoc(doc(db, "gameStats", String(game)), {starts: increment(1)}, {merge:"true"}).then(() => {
    });
}

// anonymous per-day archive usage counters, kept on the doc of the day's game
// (currentGame) so one doc read shows that day's archive activity
export function logArchiveEvent(currentGame, field) {
    setDoc(doc(db, "gameStats", String(currentGame)), {[field]: increment(1)}, {merge: true}).catch(err => {
        console.error("can't log archive event", err);
    });
}

const maxHighScores = 10;
export function checkAndLogHighScore(game, identifier, name, score, encodedMoves, scoreResponse) {
    // Read-modify-write of the highScores array must be atomic: two players finishing at
    // the same time would otherwise each read the same list and clobber the other's entry.
    // runTransaction re-runs this callback against a fresh read whenever the doc changed
    // between the read and the commit.
    const ref = doc(db, "gameStats", String(game));
    runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        let highScores = (snap.exists() && snap.data().highScores) ? snap.data().highScores : [];

        if ( score ) {
            let oldNumScores = 0;
            let lastScore = 0;
            for ( let i=0 ; i<highScores.length ; i++ ) {
                if ( highScores[i].score != lastScore ) {
                    oldNumScores++;
                    lastScore = highScores[i].score;
                }
            }

            if ( oldNumScores <= maxHighScores || score >= highScores[highScores.length-1].score ) {
                console.log("new high score");
                // user got a high score
                highScores.push({score:score, name:name, game: game, moves: encodedMoves, id: identifier});
                highScores.sort((a, b) => {if (a.score > b.score) return -1; else return 1;});

                // limit number of high scores to a unique number of scores, not a unique number of entries
                let numScores = 0;
                lastScore = 0;
                for ( let i=0 ; i<highScores.length ; i++ ) {
                    if ( highScores[i].score != lastScore ) {
                        if ( numScores >= maxHighScores ) {
                            highScores.length = i;
                            break;
                        }
                        numScores++;
                        lastScore = highScores[i].score;
                    }
                }

                tx.set(ref, {highScores: highScores}, {merge: true});
            }
        }
        return highScores;
    }).then((highScores) => {
        scoreResponse(highScores);
    }).catch( err => {
        console.error("can't update highscores", err);
    });
}

// called when user updates their name
export function updateHighScoreName(game, identifier, name, scoreResponse) {
    // same atomic read-modify-write as checkAndLogHighScore, so a concurrent score
    // insertion can't be lost by a name edit writing back a stale array.
    const ref = doc(db, "gameStats", String(game));
    runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        let highScores = (snap.exists() && snap.data().highScores) ? snap.data().highScores : [];

        let changed = false;
        for ( let i=0 ; i<highScores.length ; i++ ) {
            if ( highScores[i].id == identifier ) {
                highScores[i].name = name;
                changed = true;
                break;
            }
        }
        if ( changed )
            tx.set(ref, {highScores: highScores}, {merge: true});
        return highScores;
    }).then((highScores) => {
        scoreResponse(highScores);
    }).catch( err => {
        console.error("can't update highscores", err);
    });
}

export function logGameResults(game, goalScore, score, duration) {
    logAnalytics('game_done', {game: game});

    let upd = {};
    if ( duration < 60 )
        upd["time0"] = increment(1);
    else if ( duration >= 60 && duration < 120 )
        upd["time1"] = increment(1);
    else if ( duration >= 120 && duration < 180 )
        upd["time2"] = increment(1);
    else if ( duration >= 180 && duration < 240 )
        upd["time3"] = increment(1);
    else if ( duration >= 240 )
        upd["time4"] = increment(1);
    upd["goal"+defs.scoreLevel(goalScore, score)] = increment(1);

    updateDoc(doc(db, "gameStats", String(game)), upd).then(() => {
        //console.log("game logged");
    }).catch( err => {
        // doesn't exist, so create
        setDoc(doc(db, "gameStats", String(game)), {}).then(() =>{
            //console.log("game logged, created");
            updateDoc(doc(db, "gameStats", String(game)), upd).catch(err2 => {
                console.error("can't log game results", err2);
            });
        }).catch(err2 => {
            console.error("can't create gameStats doc", err2);
        });
    });

    // log same results to local storage
    let stats = ls.get("gamestats");
    if ( !stats ) {
        stats = {goal0: 0, goal1: 0, goal2: 0, goal3: 0};
    }
    stats["goal"+defs.scoreLevel(goalScore, score)]++;
    ls.set("gamestats", stats);
}

export function getGameResults(game, response) {
    const docRef = doc(db, "gameStats", String(game));
    const docSnap = getDocFromServer(docRef).then((docSnap) => {
        if (docSnap.exists()) {
            //console.log("Document data:", game, docSnap.data());
            response(docSnap.data());
        } else {
            // doc.data() will be undefined in this case
            //console.log("No such document!");
        }
    });
}

export function getPracticeGames(response) {
    const q = query(collection(db, "practicegames"));

    getDocs(q).then((querySnapshot) => {
        let games = [];
        querySnapshot.forEach((doc) => {
            games.push(doc.id);
        });
        response(games);
    });
}
